import React, {
	useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import {
	DndContext, DragOverlay, MouseSensor, useSensor, useSensors, DragStartEvent, DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { Virtuoso, ListRange } from 'react-virtuoso';
import { GreenroomState } from '../../stores/types/types';
import Socket from '../../connection/socket';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import type { PresentedMediaItem } from '../../stores/types/media';
import useShots from '../../contexts/shots/provider';
import * as MediaActions from '../../stores/actions/media';
import type { GreenroomUser } from '../../stores/types/session';
import useMedia from '../../hooks/use-media';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { virtualizedVerticalSortingStrategy } from '../../utils/virtualized-vertical-list-sorting-strategy';
import { pptxDeckAlignSlides } from '../../utils/pptx-deck-animations-reconcile';
import SortableMediaSlide from './media-slide-item';

interface Props {
	socket: Socket | null;
	mediaId: number | null;
	clearMediaSelectedForSlideView: Function;
	openMediaModal: Function;
	user: GreenroomUser | null;
	isHost: boolean;
	onPresent: (mediaId: number, page: number) => void;
	stopPresenting: (mediaId: number) => void;
}

export default function MediaSlidesView(props: Props) {
	const dispatch = useDispatch();
	const sensors = useSensors(useSensor(MouseSensor));
	const [activeId, setActiveId] = useState<string | null>(null);
	const {
		mediaId, clearMediaSelectedForSlideView, openMediaModal, onPresent, socket,
	} = props;

	const {
		canEditOrControlMedia,
		canDeleteMedia,
	} = useMedia();

	const presentedMedia = useSelector((state: GreenroomState) => state.MediaReducer.presentedMedia);
	const showbuilderMedia = useSelector((s: GreenroomState) => s.MediaReducer.showbuilderMedia);
	const roomState = useSelector((s: GreenroomState) => s.MeetingReducer.roomState);
	const shotStartTimestampEntity = useSelector(
		(state: GreenroomState) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);

	const baseMediaItem = useSelector(
		(state: GreenroomState) => state.MediaReducer.media.find((m) => m.id === mediaId),
	);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const firstSlideIndex = useSelector((s: GreenroomState) => s.MediaReducer.slideViewMediaIndex);

	const {
		currentShot: {
			shot_data: {
				shotFeeds,
			},
		},
		isShotBuilderOpen,
	} = useShots();

	const [userCanControl, userCanEdit, userCanDelete] = useMemo(
		() => [
			canEditOrControlMedia(baseMediaItem!, props.user, props.isHost, true),
			canEditOrControlMedia(baseMediaItem!, props.user, props.isHost, false),
			canDeleteMedia(baseMediaItem!, props.user, props.isHost)],
		[props.isHost, baseMediaItem, props.user, shotFeeds],
	);

	const hasSwitchSlidesControl = userCanControl || userCanEdit || userCanDelete;

	const switchingDisabled = !hasSwitchSlidesControl
		|| roomState === ROOM_STATES.EDITING
		|| isShotBuilderOpen;

	const presentedMediaItem = useMemo(() => (
		roomState === ROOM_STATES.EDITING ? showbuilderMedia : presentedMedia).find(
		(m) => m.mediaId === mediaId,
	), [mediaId, presentedMedia, showbuilderMedia, roomState]);

	const isInCanvas = useMemo(() => shotFeeds.find(
		(f) => f.media === mediaId,
	), [mediaId, shotFeeds]);

	/* this is a duplicte fn in feed-media.tsx, media-slides-view.tsx, and video-feed-thumbnail.tsx */
	const changePage = useCallback((newPage: number) => {
		if (!mediaId || switchingDisabled) return;

		if (!presentedMediaItem) {
			onPresent(mediaId, newPage);
		} else {
			const newVideoState = { paused: !shotStartTimestampEntity, timeElapsed: 0, volume: 1 };
			dispatch(
				MediaActions.setPresentedMediaPage(mediaId, newPage, newVideoState),
			);
		}
	}, [mediaId,
		presentedMediaItem,
		shotStartTimestampEntity,
		switchingDisabled,
	]);

	const onDragStart = (event: DragStartEvent) => {
		setActiveId(String(event.active.id));
	};

	const onDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		setActiveId(null);

		if (!over || !baseMediaItem || active.id === over.id) return;

		const oldPages = baseMediaItem.media_data.image_array;
		const oldIndex = oldPages.indexOf(String(active.id));
		const newIndex = oldPages.indexOf(String(over.id));

		if (oldIndex === -1 || newIndex === -1) return;

		// the slide currently in view — keep it selected wherever it lands,
		// regardless of which slide was dragged
		const currentPageUrl = oldPages[(presentedMediaItem?.currentPage ?? 1) - 1];

		const newPages = arrayMove(oldPages, oldIndex, newIndex);
		// For DOP decks: media_data.slides carries each slide's physical DOP
		// index/animation metadata at the same position as image_array — it must
		// move with the same oldIndex/newIndex or a slide's animations stay
		// behind at its old position while its thumbnail/content move. No-op for
		// non-DOP media, which never has `slides`. Aligned first so dragging an
		// added page (past the end of `slides`) moves its `null`, not a hole.
		const oldSlides = baseMediaItem.media_data.slides;
		const newSlides = oldSlides
			&& arrayMove(pptxDeckAlignSlides(oldSlides, oldPages.length), oldIndex, newIndex);

		const updatedMedia = {
			...baseMediaItem,
			images: newPages.join(),
			media_data: {
				...baseMediaItem.media_data,
				image_array: newPages,
				...(newSlides ? { slides: newSlides } : {}),
			},
		};

		// optimistically reflect the new order so the current slide stays in sync
		// (the async save only updates the store once the API responds)
		dispatch(MediaActions.updateSingleMedia(updatedMedia));

		// keep the previously-current slide highlighted at its new position
		const retainedIndex = newPages.indexOf(currentPageUrl);
		if (retainedIndex !== -1) {
			const updatedPresentedMedia = presentedMedia.map((med: PresentedMediaItem) => (
				med.mediaId === mediaId ? { ...med, currentPage: retainedIndex + 1 } : med
			));
			dispatch(MediaActions.setPresentedMedia(updatedPresentedMedia));
		}

		if (token) {
			dispatch(MediaActions.saveMediaItem(token, updatedMedia, undefined));
		}
		// Same as the media modal's reorder: other clients get the new order now
		// rather than after the save's media-updated-single refetch.
		socket?.setMedia(updatedMedia);
	};

	const mediaPages = useMemo((): JSX.Element[] | undefined => baseMediaItem?.media_data.image_array
		.map((pageUrl: string, index: number) => {
			const url = baseMediaItem?.media_data.thumbnail_map[pageUrl] ?? pageUrl;
			// currentPage is indexed at 1
			const isCurrentPage = index === (presentedMediaItem?.currentPage ?? 1) - 1;

			return <SortableMediaSlide
				key={pageUrl}
				id={pageUrl}
				index={index}
				url={url}
				isCurrentPage={isCurrentPage}
				switchingDisabled={switchingDisabled}
				isInCanvas={!!isInCanvas}
				fill={baseMediaItem?.media_data.fill}
				backgroundColor={baseMediaItem?.media_data.background_color}
				onSelect={() => changePage(index + 1)}
			/>;
		}), [
		baseMediaItem?.media_data,
		presentedMediaItem?.currentPage,
		changePage,
		switchingDisabled,
		isInCanvas,
	]);

	useEffect(() => {
		if (!mediaPages) {
			clearMediaSelectedForSlideView();
		}
	}, [mediaPages]);

	const onRangeChanged = (range: ListRange) => {
		dispatch(MediaActions.setSlideViewMediaIndex(range.startIndex));
	};

	// preview rendered by DragOverlay so the dragged slide follows the cursor
	// without being clipped by the container or unmounted by virtualization
	const activeSlide = useMemo(() => {
		if (!activeId || !baseMediaItem) return null;
		const index = baseMediaItem.media_data.image_array.indexOf(activeId);
		if (index === -1) return null;
		return {
			index,
			url: baseMediaItem.media_data.thumbnail_map[activeId] ?? activeId,
		};
	}, [activeId, baseMediaItem?.media_data]);

	return (
		<div className="media-slides-view">
			<div className="media-slides-header">
				<div onClick={() => clearMediaSelectedForSlideView()} className="media-slides-header-all">
					<Icon name={ICONS.ARROW_LEFT} color={COLORS.WHITE} size={16}></Icon>
					<span>All</span>
				</div>

				<div onClick={() => openMediaModal(mediaId)} className="media-slides-header-preview">
					<Icon name={ICONS.FULLSCREEN} color={COLORS.PALE_GREEN} size={16}></Icon>
					<span>Preview</span>
				</div>
			</div>
			<div className="media-slides-pages-view">
				{mediaPages && (
					<div className="virtuoso-scroller">
						<DndContext
							sensors={sensors}
							onDragStart={onDragStart}
							onDragEnd={onDragEnd}
							onDragCancel={() => setActiveId(null)}
						>
							<SortableContext
								items={baseMediaItem?.media_data.image_array ?? []}
								strategy={virtualizedVerticalSortingStrategy}
							>
								<Virtuoso
									rangeChanged={onRangeChanged}
									style={{ height: '100%' }}
									totalCount={mediaPages?.length}
									itemContent={(index) => mediaPages?.[index] ?? <div style={{ minHeight: '1px' }}/>}
									initialTopMostItemIndex={firstSlideIndex}
								/>
							</SortableContext>
							<DragOverlay className="dnd-grabbing-cursor">
								{activeSlide ? (
									<div className="media-slide-container">
										<div className="media-slides-handle">
											<Icon
												name={ICONS.THREE_DOTS_VERTICAL}
												color={COLORS.DEFAULT_GRAY}
												size={14}></Icon>
											{activeSlide.index + 1}
										</div>
										<div className="media-slide">
											<div
												className={classNames('thumbnail-container', baseMediaItem?.media_data.fill && 'fill')}
												style={{ background: baseMediaItem?.media_data.background_color }}
											>
												<img src={activeSlide.url} alt="dragging slide" />
											</div>
										</div>
									</div>
								) : null}
							</DragOverlay>
						</DndContext>
					</div>
				)}
			</div>
		</div>
	);
}
